import { PrintJob, buildReceiptBytes } from './bluetoothPrinter';
import html2canvas from 'html2canvas';

const USB_PRINTER_NAME_KEY = 'usb_printer_name';

function removeToast() {
  document.getElementById('bt-toast')?.remove();
}

function showToast(msg: string, color = '#1e293b', autoDismiss = 0) {
  removeToast();
  const t = document.createElement('div');
  t.id = 'bt-toast'; // reuse the same toast styles injected by bluetoothPrinter.ts
  t.style.cssText = `position:fixed;top:24px;left:50%;transform:translateX(-50%);background:${color};color:#fff;padding:14px 22px;border-radius:14px;font-family:system-ui,sans-serif;z-index:100000;box-shadow:0 8px 32px rgba(0,0,0,0.35);font-size:13px;font-weight:600;min-width:280px;text-align:center;`;
  t.textContent = msg;
  document.body.appendChild(t);
  if (autoDismiss) setTimeout(removeToast, autoDismiss);
}

let usbDevice: any = null;
let usbEndpointOut: number | null = null;

async function claimUsbDevice(device: any): Promise<boolean> {
  try {
    await device.open();
    if (device.configuration === null) await device.selectConfiguration(1);
    const iface = device.configuration.interfaces.find((i: any) =>
      i.alternates.some((a: any) => a.endpoints.some((e: any) => e.direction === 'out'))
    );
    if (!iface) throw new Error('No writable USB endpoint found on this device');
    await device.claimInterface(iface.interfaceNumber);
    const alt = iface.alternates.find((a: any) => a.endpoints.some((e: any) => e.direction === 'out'));
    const outEp = alt.endpoints.find((e: any) => e.direction === 'out');
    usbDevice = device;
    usbEndpointOut = outEp.endpointNumber;
    return true;
  } catch (e) {
    console.error('USB claim failed:', e);
    return false;
  }
}

export async function connectUSBPrinter(): Promise<boolean> {
  const usb = (navigator as any).usb;
  if (!usb) {
    alert('WebUSB is not supported in this browser. Use Chrome or Edge (desktop or Android).');
    return false;
  }

  showToast('🔍 Opening USB device picker...', '#1e293b');
  try {
    const device = await usb.requestDevice({ filters: [] });
    removeToast();
    showToast(`🔗 Connecting to ${device.productName || 'USB printer'}...`, '#1e293b');

    const ok = await claimUsbDevice(device);
    removeToast();
    if (!ok) {
      alert('Connected but could not claim a writable USB interface on this device.');
      return false;
    }
    localStorage.setItem(USB_PRINTER_NAME_KEY, device.productName || 'USB Printer');
    return true;
  } catch (e: any) {
    removeToast();
    if (e?.name !== 'NotFoundError') console.error('USB connect failed:', e);
    return false;
  }
}

// Silent reconnect to a previously-granted USB device — no picker, no user click needed.
// Call this on app load so a printer connected in a prior session just works again.
export async function tryAutoReconnectUSB(): Promise<boolean> {
  const usb = (navigator as any).usb;
  if (!usb) return false;
  try {
    const devices = await usb.getDevices();
    if (devices.length === 0) return false;
    return await claimUsbDevice(devices[0]);
  } catch {
    return false;
  }
}

export function isUSBPrinterConnected(): boolean {
  return !!usbDevice;
}

export function disconnectUSBPrinter() {
  usbDevice?.close?.().catch(() => {});
  usbDevice = null;
  usbEndpointOut = null;
  localStorage.removeItem(USB_PRINTER_NAME_KEY);
}

export function getUSBPrinterName(): string | null {
  return localStorage.getItem(USB_PRINTER_NAME_KEY);
}

async function sendUSB(bytes: Uint8Array) {
  if (!usbDevice || usbEndpointOut === null) throw new Error('No USB printer connected');
  const CHUNK = 4096;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    await usbDevice.transferOut(usbEndpointOut, bytes.slice(i, i + CHUNK));
  }
}

const DEFAULT_PRINTER_DOTS_WIDTH = 576; // 576 = 80mm, 384 = 58mm (at 203dpi)

async function elementToRasterBytes(
  elementId: string,
  printerWidthDots = DEFAULT_PRINTER_DOTS_WIDTH
): Promise<number[]> {
  const el = document.getElementById(elementId);
  if (!el) throw new Error(`Element #${elementId} not found`);

  const canvas = await html2canvas(el, {
    backgroundColor: '#ffffff',
    scale: 1, // lower scale = smaller payload, less chance of overwhelming the printer buffer
    useCORS: true,
    foreignObjectRendering: true,
  });

  const scale = printerWidthDots / canvas.width;
  const targetH = Math.max(1, Math.round(canvas.height * scale));

  const off = document.createElement('canvas');
  off.width = printerWidthDots;
  off.height = targetH;
  const ctx = off.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, off.width, off.height);
  ctx.drawImage(canvas, 0, 0, off.width, off.height);

  const { data } = ctx.getImageData(0, 0, off.width, off.height);
  const widthBytes = Math.ceil(printerWidthDots / 8);
  const pixels = new Uint8Array(widthBytes * targetH);

  for (let y = 0; y < targetH; y++) {
    for (let x = 0; x < printerWidthDots; x++) {
      const i = (y * printerWidthDots + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
      const lum = r * 0.299 + g * 0.587 + b * 0.114;
      const isBlack = a > 10 && lum < 200;
      if (isBlack) {
        const byteIndex = y * widthBytes + Math.floor(x / 8);
        const bit = 7 - (x % 8);
        pixels[byteIndex] |= 1 << bit;
      }
    }
  }

  const xL = widthBytes & 0xff, xH = (widthBytes >> 8) & 0xff;
  const yL = targetH & 0xff, yH = (targetH >> 8) & 0xff;

  console.log(`[USB PRINT] raster built: ${printerWidthDots}x${targetH} dots, ${pixels.length} image bytes`);
  return [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...Array.from(pixels)];
}

export async function printElementUSB(
  elementId: string,
  printerWidthDots = DEFAULT_PRINTER_DOTS_WIDTH
): Promise<boolean> {
  try {
    if (!isUSBPrinterConnected()) {
      const connected = await connectUSBPrinter();
      if (!connected) return false;
    }

    showToast('📸 Rendering receipt...', '#1e293b');
    const raster = await elementToRasterBytes(elementId, printerWidthDots);

    showToast('📄 Printing receipt...', '#1d4ed8');
    const FEED = [0x1B, 0x64, 0x04];
    const CUT = [0x1D, 0x56, 0x41, 0x10];
    const fullPayload = new Uint8Array([...raster, ...FEED, ...CUT]);
const CHUNK_SIZE = 512;
const totalChunks = Math.ceil(fullPayload.length / CHUNK_SIZE);
console.log(`[USB PRINT] payload=${fullPayload.length} bytes, chunks=${totalChunks}`);

for (let i = 0; i < fullPayload.length; i += CHUNK_SIZE) {
  const chunk = fullPayload.slice(i, i + CHUNK_SIZE);
  const chunkNum = Math.floor(i / CHUNK_SIZE) + 1;
  try {
    console.log(`[USB PRINT] sending chunk ${chunkNum}/${totalChunks} (${chunk.length} bytes)`);
    await sendUSB(chunk);
    console.log(`[USB PRINT] chunk ${chunkNum} OK`);
  } catch (chunkErr) {
    console.error(`[USB PRINT] chunk ${chunkNum} FAILED:`, chunkErr);
    throw chunkErr;
  }
  await new Promise(res => setTimeout(res, 15));
}
console.log('[USB PRINT] all chunks sent');

    showToast('✅ Printed successfully!', '#15803d', 2500);
    return true;
  } catch (e) {
    removeToast();
    console.error('USB image print failed:', e);
    alert(`Print failed: ${(e as Error).message}`);
    return false;
  }
}

export async function printReceiptUSB(job: PrintJob): Promise<boolean> {
  try {
    if (!isUSBPrinterConnected()) {
      const connected = await connectUSBPrinter();
      if (!connected) return false;
    }

    showToast('📄 Printing receipt...', '#1d4ed8');
    const bytes = new Uint8Array(buildReceiptBytes(job));
    await sendUSB(bytes);

    showToast('✅ Printed successfully!', '#15803d', 2500);
    return true;
  } catch (e) {
    removeToast();
    console.error('USB print failed:', e);
    return false;
  }
}